import React, { useState, useEffect } from "react";

import { Grid, MenuItem } from "@mui/material";
import { DirectionsBoatOutlined as DirectionsBoatOutlinedIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { IoAirplaneOutline } from "react-icons/io5";
import { RiTruckLine } from "react-icons/ri";

import { useAppState } from "../../../../context/StateContext";
import { Destination } from "../../../../models/destination";
import { JobMode } from "../../../../models/job.mode";
import { DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import ModeJobService from "../../../../services/mode.job.service";
import { Mode, Status } from "../../../../util/enums";
import { getValueFromEachDestination } from "../../../../util/getDestinations";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import { isAllied } from "../../../../util/isAllied";
import Input from "../../../Form/Input";
import InputRange from "../../../Form/InputRange";
import Select from "../../../Form/Select";
import Modal from "../../Modal";
import SkeletonComponent from "../../Skeleton";
import useStyles, { SectionWrap } from "./styles";

interface EditMaterialModalProps {
  material: Material | null;
  locations: LocationAddress[];
  openModal: boolean;
  destinations: Destination[];
  closeModal: () => void;
  submit: (material: Material) => void;
}

const EditMaterialModal = ({
  material,
  openModal,
  locations,
  destinations,
  closeModal,
  submit,
}: EditMaterialModalProps) => {
  const { currentUser } = useAppState();
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const [materialSelected, setMaterialSelected] = useState<Material | null>();
  const [jobModeList, setJobModeList] = useState<JobMode[]>([]);

  const { isFetching: isFetchingMode } = useQuery(
    ["jobMode.list"],
    () =>
      ModeJobService.getAll({
        status: Status.ACTIVE,
        companyId: currentUser?.companyId,
      }),
    {
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setJobModeList(response.data);
        }
      },
    }
  );

  useEffect(() => {
    setMaterialSelected(material);
  }, [material]);

  const handleSubmitChanges = () => {
    if (materialSelected) {
      submit(materialSelected);
    }
    closeModal();
  };

  const isActive = (mode: string): boolean => mode === materialSelected?.mode;

  const renderModeItem = (title: string, isActive: boolean, icon?: JSX.Element, label?: string) => {
    const updateModeAndDestination = () => {
      if (!materialSelected) return;
      const modeFreight: string = `${title} freight`;

      const destinationFoundByName =
        destinations && destinations?.length > 0
          ? destinations?.find(
              (destination) => destination?.id && destination?.name === modeFreight
            )
          : null;

      let material: Material = { ...materialSelected, mode: title };

      if (
        destinationFoundByName &&
        destinationFoundByName?.id &&
        !destinationFoundByName?.destinationResidenceId &&
        !destinationFoundByName?.modeDestinationId
      ) {
        material = {
          ...material,
          destination:
            destinationFoundByName &&
            destinationFoundByName?.id &&
            !destinationFoundByName?.destinationResidenceId &&
            !destinationFoundByName?.modeDestinationId
              ? destinationFoundByName
              : undefined,
          destinationId:
            destinationFoundByName &&
            destinationFoundByName?.id &&
            !destinationFoundByName?.destinationResidenceId &&
            !destinationFoundByName?.modeDestinationId
              ? destinationFoundByName?.id
              : null,
          destinationResidenceId: null,
          destinationResidence: undefined,
          modeDestination: undefined,
          modeDestinationId: null,
        } as Material;
      }

      setMaterialSelected(material);
    };

    return (
      <div
        key={title}
        className={classnames(
          classes.modalEditItemModeBox,
          isActive ? classes.modalEditItemModeBoxActive : ""
        )}
        onClick={() => updateModeAndDestination()}
      >
        {icon && icon}
        <span className={classnames(classes.titleMode, isActive ? classes.titleModeActive : "")}>
          {label || title}
        </span>
      </div>
    );
  };

  const getDestinationByMaterialSelected = () => {
    if (materialSelected && materialSelected?.destination && materialSelected?.destinationId) {
      return materialSelected?.destination?.name;
    }

    if (
      materialSelected &&
      materialSelected?.modeDestination &&
      materialSelected?.modeDestinationId
    ) {
      return materialSelected?.modeDestination?.name;
    }

    if (
      materialSelected &&
      materialSelected?.destinationResidence &&
      materialSelected?.destinationResidenceId
    ) {
      return `Residence: ${materialSelected?.destinationResidence?.longAddress}`;
    }

    return "";
  };

  const getMaterialByDestination = (destination?: Destination) => {
    if (destination) {
      let mode = "";

      if (destination?.name?.toLocaleLowerCase().includes("freight")) {
        const modeSplit = destination?.name?.split(" ");

        if (modeSplit && modeSplit.length > 0) {
          const [destinationName] = modeSplit;

          mode = destinationName;
        }
      }

      if (isAllied(currentUser?.company)) {
        if (destination?.destinationResidenceId) {
          return Mode.Road;
        }
      }

      if (mode) return mode;
    }

    return materialSelected && materialSelected?.mode ? materialSelected?.mode : undefined;
  };

  const getModeIcon = (title: string) => {
    switch (title) {
      case Mode.Road:
        return (
          <RiTruckLine className={classes.icon} color={isActive(title) ? "#308CDB" : "#93A9BD"} />
        );
      case Mode.Air:
        return (
          <DirectionsBoatOutlinedIcon
            className={classes.icon}
            htmlColor={isActive(title) ? "#308CDB" : "#93A9BD"}
          />
        );
      case Mode.Sea:
        return (
          <IoAirplaneOutline
            className={classes.icon}
            color={isActive(title) ? "#308CDB" : "#93A9BD"}
          />
        );
      default:
        return undefined;
    }
  };

  return (
    <Modal
      open={openModal}
      handleClose={closeModal}
      data-cy="editMaterialModal"
      title={translate("inventory")}
      showActionButtons
      actionButtonProps={{
        onSave: handleSubmitChanges,
        dataCySave: "submitChanges",
        onCancel: closeModal,
        saveDisabled: !!(
          !materialSelected?.destinationId &&
          !materialSelected?.destinationResidenceId &&
          !materialSelected?.modeDestinationId
        ),
      }}
    >
      <span className={classes.modalEditItemBodyTitle}>{translate("materialDetails")}</span>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Input
            fullWidth
            name="material_description"
            value={materialSelected?.serviceBooking.name}
            label={translate("description")}
            disabled
          />
        </Grid>
        {!isAllied(currentUser?.company) && (
          <Grid item xs={12}>
            <span className={classes.modalEditItemModeLabel}>{translate("mode")}</span>

            <SkeletonComponent isLoading={isFetchingMode}>
              <SectionWrap gap="16px">
                {jobModeList.map((mode) => {
                  return renderModeItem(mode.name, isActive(mode.name), getModeIcon(mode.name), translate(mode.value.toLowerCase()) || mode.name);
                })}
              </SectionWrap>
            </SkeletonComponent>
          </Grid>
        )}
        <Grid item xs={12}>
          <SectionWrap gap="32px">
            <InputRange
              decimalPlacesMaxLength={0}
              label={translate("quantity")}
              value={materialSelected?.quantity || 0}
              change={(value: number) =>
                materialSelected && setMaterialSelected({ ...materialSelected, quantity: value })
              }
              range={1}
              onlyIntValues
            />
            <InputRange
              label={translate("volume")}
              value={materialSelected?.volume || 0}
              measurementInputRange={measurement}
              change={(value: number) =>
                materialSelected &&
                setMaterialSelected({
                  ...materialSelected,
                  volume: value,
                })
              }
              minValue={0}
            />
            <InputRange
              label={translate("weight")}
              measurementInputRange={{
                code: measurementWeight?.code || "kg",
                decimalPlaces: measurementWeight?.decimalPlaces,
              }}
              value={materialSelected?.weight || 0}
              minValue={0}
              change={(value: number) => {
                if (materialSelected) {
                  setMaterialSelected({
                    ...materialSelected,
                    weight: value,
                  });
                }
              }}
            />
          </SectionWrap>
        </Grid>
        <Grid item xs={12}>
          <Select
            data-cy="destinationMaterialModal"
            name="destination"
            label={translate("destination")}
            value={getDestinationByMaterialSelected()}
            onChange={(event) => {
              if (!materialSelected) return;
              const destination = destinations?.find(
                (destination) => destination.name === event.target.value
              );

              setMaterialSelected({
                ...materialSelected,

                destination: getValueFromEachDestination(destination)?.destination,
                destinationId: getValueFromEachDestination(destination)?.destination?.id || null,

                destinationResidence: getValueFromEachDestination(destination, locations)
                  ?.destinationResidence,
                destinationResidenceId:
                  getValueFromEachDestination(destination, locations)?.destinationResidence?.id ||
                  null,

                modeDestination: getValueFromEachDestination(destination)?.modeDestination,
                modeDestinationId:
                  getValueFromEachDestination(destination)?.modeDestination?.id || null,

                mode: getMaterialByDestination(destination),
              });
            }}
            placeholder={translate("chooseADestination")}
          >
            {destinations &&
              destinations?.map((destination, index: number) => (
                <MenuItem
                  data-cy={`${index}-destination-option`}
                  key={destination.name || index}
                  value={destination.name}
                >
                  {destination.name}
                </MenuItem>
              ))}
          </Select>
        </Grid>
        <Grid item xs={12}>
          <Input
            name="material-description"
            label={translate("comments")}
            value={materialSelected?.comments ?? ""}
            multiline
            maxRows={5}
            onChange={(event) =>
              materialSelected &&
              setMaterialSelected({ ...materialSelected, comments: event.target.value })
            }
          />
        </Grid>
      </Grid>
    </Modal>
  );
};

export default EditMaterialModal;
